'use client'

import { ArrowRight, Check } from 'lucide-react'
import { formatLandingPrice, showLandingGstNote } from '@/lib/formatLandingPrice'
import { isDussehraOfferPack } from '@/lib/productPacks'
import {
  getComboFeatures,
  getPackConfigKey,
  sortPacksComboCenter,
  useComboPricing,
} from '../hooks/useComboPricing'
import { PaymentRequiredModal } from '@/components/ui/PaymentRequiredModal'
import { PaymentSuccessModal } from '@/components/ui/PaymentSuccessModal'
import { PaymentPendingModal } from '@/components/ui/PaymentPendingModal'
import styles from '../newLanding.module.css'
import { LandingRevealOnView } from './LandingRevealOnView'
import { useCountUp } from '../hooks/useCountUp'

function ComboPriceRow({
  price,
  originalPrice,
  currency,
}: {
  price: number
  originalPrice: number | null
  currency: string
}) {
  const animatedPrice = useCountUp(price)
  const hasDiscount = originalPrice != null && originalPrice > price
  const discountPct = hasDiscount
    ? Math.round(((originalPrice - price) / originalPrice) * 100)
    : 0

  return (
    <div className={styles.comboPriceBlock}>
      {hasDiscount ? (
        <div className={styles.comboOriginalRow}>
          <span className={styles.comboOriginalPrice}>
            {formatLandingPrice(originalPrice, currency)}
          </span>
          <span className={styles.comboDiscountBadge}>{discountPct}% OFF</span>
        </div>
      ) : null}
      <div className={styles.comboPriceRow}>
        <span className={styles.comboAmount}>
          {formatLandingPrice(animatedPrice ?? price, currency)}
        </span>
        {showLandingGstNote(currency) ? <span className={styles.comboGst}>+GST</span> : null}
      </div>
    </div>
  )
}

export function NewUIComboPlans() {
  const {
    visible,
    packs,
    selectedPack,
    isModalOpen,
    isProcessing,
    showSuccessModal,
    setShowSuccessModal,
    showPendingModal,
    setShowPendingModal,
    handleBuy,
    handleUnlock,
    handleCancel,
  } = useComboPricing()

  const catalogPacks = packs.filter((pack) => !isDussehraOfferPack(pack))

  if (!visible || catalogPacks.length === 0) return null

  return (
    <>
      <section className={styles.comboSection} aria-labelledby="combo-plans-heading">
        <div className={styles.comboInner}>
          <LandingRevealOnView className={styles.comboHeader}>
            <span className={styles.heroBadge}>Combo Plans</span>
            <h2 id="single-plans-heading" className={styles.singlePlansHeading}>
              Simple, Transparent Plans.
            </h2>
            <p className={styles.comboSubheading}>
              Choose the pack that fits your interview preparation goals.
            </p>
          </LandingRevealOnView>

          <div className={styles.comboGrid}>
            {sortPacksComboCenter(catalogPacks).map((pack, index) => {
              const featured = getPackConfigKey(pack) === 'combo_advanced'
              const features = getComboFeatures(pack)
              return (
                <LandingRevealOnView
                  key={pack.product_code}
                  as="article"
                  className={`${styles.comboCard} ${featured ? styles.comboCardFeatured : ''}`}
                  delayMs={index * 120}
                >
                  <div className={styles.comboCardHead}>
                    {featured ? (
                      <span className={styles.comboBadgeRecommended}>Recommended</span>
                    ) : null}
                    <p className={styles.comboTierLabel}>{pack.product_name}</p>
                  </div>

                  <ComboPriceRow
                    price={pack.display_price}
                    originalPrice={pack.original_price}
                    currency={pack.currency}
                  />

                  <button
                    type="button"
                    className={featured ? styles.comboCtaFeatured : styles.comboCta}
                    onClick={() => handleBuy(pack)}
                    aria-label={`Buy ${pack.product_name} for ${formatLandingPrice(pack.display_price, pack.currency)}`}
                  >
                    Buy Now
                    <ArrowRight size={16} className={styles.ctaArrowIcon} aria-hidden="true" />
                  </button>

                  <ul className={styles.comboFeatureList} aria-label="Included credits">
                    {features.map((line) => (
                      <li key={line} className={styles.comboFeatureRow}>
                        <span className={styles.comboCheckIcon} aria-hidden="true">
                          <Check size={12} strokeWidth={3} />
                        </span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </LandingRevealOnView>
              )
            })}
          </div>
        </div>
      </section>

      <PaymentRequiredModal
        isOpen={isModalOpen}
        isProcessing={isProcessing}
        productPrice={selectedPack?.price ?? null}
        productName={selectedPack?.product_name ?? null}
        onUnlock={() => void handleUnlock()}
        onCancel={handleCancel}
      />

      {showSuccessModal && (
        <PaymentSuccessModal
          isOpen={showSuccessModal}
          attemptsAdded={1}
          onContinue={() => setShowSuccessModal(false)}
        />
      )}
      <PaymentPendingModal
        isOpen={showPendingModal}
        onClose={() => setShowPendingModal(false)}
      />
    </>
  )
}
